<script lang="ts">
    import { onMount } from "svelte";
    import { apiClient, type FlatOrderModel } from "./apiClient";
    import { error, success } from "./stores";

    let historicOrders: FlatOrderModel[] = [];
    let loading = true;

    onMount(async () => {
        await loadHistoricOrders();
    });

    async function loadHistoricOrders() {
        try {
            loading = true;
            error.set(null);
            const orders = await apiClient.getOrders("done", true);

            // Sort orders by completion date (done), most recently completed first
            historicOrders = [...orders].sort((a, b) => {
                const completionA = new Date(a.done || a.created).getTime();
                const completionB = new Date(b.done || b.created).getTime();
                return completionB - completionA;
            });
        } catch (err) {
            const errorMessage =
                err instanceof Error ? err.message : "Unknown error";
            error.set(`Failed to load historic orders: ${errorMessage}`);
            console.error("Error loading historic orders:", err);
        } finally {
            loading = false;
        }
    }

    async function restoreOrder(orderId: number) {
        try {
            error.set(null);
            await apiClient.restoreOrder(orderId);
            success.set(`Order #${orderId} has been restored successfully!`);

            // Remove the restored order from the list
            historicOrders = historicOrders.filter(
                (order) => order.id !== orderId,
            );
        } catch (err) {
            const errorMessage =
                err instanceof Error ? err.message : "Unknown error";
            error.set(`Failed to restore order #${orderId}: ${errorMessage}`);
            console.error("Error restoring order:", err);
        }
    }

    function formatDateTime(dateTime: string): string {
        return new Date(dateTime).toLocaleString();
    }

    function formatOrderItems(items: any[]): string {
        return items.map((item) => `${item.amount}x ${item.drink}`).join(", ");
    }
</script>

<div class="historic-orders">
    <h2>Historic Orders</h2>

    {#if loading}
        <div class="loading">Loading historic orders...</div>
    {:else if historicOrders.length === 0}
        <div class="empty-state">
            <p>No historic orders found.</p>
        </div>
    {:else}
        <div class="orders-grid">
            {#each historicOrders as order (order.id)}
                <div class="order-card">
                    <div class="order-header">
                        <h3>Order #{order.id}</h3>
                        <span class="table-name">{order.table}</span>
                    </div>

                    <div class="order-details">
                        <div class="order-items">
                            <strong>Items:</strong>
                            <p>{formatOrderItems(order.items)}</p>
                        </div>

                        <div class="order-meta">
                            <p>
                                <strong>Created:</strong>
                                {formatDateTime(order.created)}
                            </p>
                            {#if order.accepted}
                                <p>
                                    <strong>Accepted:</strong>
                                    {formatDateTime(order.accepted)}
                                </p>
                            {/if}
                            {#if order.done}
                                <p>
                                    <strong>Completed:</strong>
                                    {formatDateTime(order.done)}
                                </p>
                            {/if}
                            {#if order.attendant}
                                <p>
                                    <strong>Attendant:</strong>
                                    {order.attendant}
                                </p>
                            {/if}
                        </div>
                    </div>

                    <div class="order-actions">
                        <button
                            class="restore-btn"
                            on:click={() => restoreOrder(order.id)}
                        >
                            🔄 Restore Order
                        </button>
                    </div>
                </div>
            {/each}
        </div>
    {/if}
</div>

<style>
    .historic-orders {
        padding: 20px 0;
    }

    .loading,
    .empty-state {
        text-align: center;
        padding: 40px;
        color: #666;
        font-size: 1.1rem;
    }

    .orders-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
        gap: 20px;
        margin-top: 20px;
    }

    .order-card {
        background: #fff;
        border: 1px solid #e1e5e9;
        border-radius: 8px;
        padding: 20px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        transition: box-shadow 0.2s ease;
    }

    .order-card:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    .order-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 15px;
        border-bottom: 1px solid #eee;
        padding-bottom: 10px;
    }

    .order-header h3 {
        margin: 0;
        color: #2c3e50;
        font-size: 1.2rem;
    }

    .table-name {
        background: #3498db;
        color: white;
        padding: 4px 12px;
        border-radius: 20px;
        font-size: 0.9rem;
        font-weight: 500;
    }

    .order-details {
        margin-bottom: 20px;
    }

    .order-items {
        margin-bottom: 15px;
    }

    .order-items strong {
        color: #2c3e50;
        display: block;
        margin-bottom: 5px;
    }

    .order-items p {
        margin: 0;
        background: #f8f9fa;
        padding: 10px;
        border-radius: 4px;
        border-left: 3px solid #3498db;
    }

    .order-meta p {
        margin: 5px 0;
        font-size: 0.9rem;
        color: #666;
    }

    .order-meta strong {
        color: #2c3e50;
    }

    .order-actions {
        display: flex;
        justify-content: flex-end;
    }

    .restore-btn {
        background: #27ae60;
        color: white;
        border: none;
        padding: 10px 20px;
        border-radius: 6px;
        cursor: pointer;
        font-size: 0.9rem;
        font-weight: 500;
        transition: background-color 0.2s ease;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .restore-btn:hover {
        background: #229954;
    }

    .restore-btn:active {
        transform: translateY(1px);
    }
</style>
